/**
 * Per-layer group-weight helpers for the calculation editor.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Bridges between the persisted per-layer `FormulaConfig.groupWeights` and the
 * editor's `PerLayerWeights` (three maps, each summing to 100). A group only
 * appears in the layers it `appliesTo` (undefined => all layers).
 */

import {
  ALL_LAYERS,
  readGroupWeight,
  type FlowLayer,
  type FormulaConfig,
  type FormulaGroup,
  type GroupWeights,
  type PerLayerGroupWeight,
} from '@trafficpulse/shared';

import {
  rebalanceAllLayers,
  type PerLayerWeights,
} from '../../hooks/auto-balanced-sliders.math';

type GroupScope = Pick<FormulaGroup, 'id' | 'appliesTo'>;

/** Layers a group scores (undefined / empty appliesTo => all three). */
export function groupLayers(group: GroupScope): ReadonlyArray<FlowLayer> {
  const scope = group.appliesTo;
  return scope && scope.length > 0 ? scope : ALL_LAYERS;
}

/** Persisted per-layer weights -> editor maps (one map per layer). */
export function buildPerLayerWeights(formula: FormulaConfig): PerLayerWeights {
  const out: PerLayerWeights = { paths: {}, landers: {}, offers: {} };
  for (const group of formula.groups) {
    for (const layer of groupLayers(group)) {
      out[layer][group.id] = readGroupWeight(formula.groupWeights, group.id, layer);
    }
  }
  return out;
}

/** Editor maps -> persisted per-layer weights (groupId -> per-layer entry). */
export function flattenPerLayerWeights(
  perLayer: PerLayerWeights,
  groups: ReadonlyArray<FormulaGroup>,
): GroupWeights {
  const out: Record<string, PerLayerGroupWeight> = {};
  for (const group of groups) {
    const entry: PerLayerGroupWeight = {};
    for (const layer of groupLayers(group)) {
      entry[layer] = perLayer[layer]?.[group.id] ?? 0;
    }
    out[group.id] = entry;
  }
  return out;
}

/** This group's weights for just the layers it applies to (for its card). */
export function pickGroupLayerWeights(
  perLayer: PerLayerWeights,
  group: GroupScope,
): PerLayerGroupWeight {
  const out: PerLayerGroupWeight = {};
  for (const layer of groupLayers(group)) {
    out[layer] = perLayer[layer]?.[group.id] ?? 0;
  }
  return out;
}

/**
 * Rebuild every layer's map from `groups` + their `appliesTo`, seeding new
 * entries (equal share when a layer was empty / summed to 0) and rebalancing
 * each layer to exactly 100. Used after add / remove / appliesTo changes.
 */
export function reconcilePerLayerWeights(
  perLayer: PerLayerWeights,
  groups: ReadonlyArray<FormulaGroup>,
): PerLayerWeights {
  const next: PerLayerWeights = { paths: {}, landers: {}, offers: {} };
  for (const layer of ALL_LAYERS) {
    next[layer] = seedLayer(perLayer[layer] ?? {}, idsForLayer(groups, layer));
  }
  return rebalanceAllLayers(next);
}

/**
 * Reconcile + rebalance a whole config's per-layer weights so every layer sums
 * to exactly 100. Tolerates legacy flat weights on input (via readGroupWeight)
 * and seeds any group missing from a layer. Used by the clipboard paste path.
 */
export function rebalanceConfig(config: FormulaConfig): FormulaConfig {
  const perLayer = reconcilePerLayerWeights(
    buildPerLayerWeights(config),
    config.groups,
  );
  return {
    ...config,
    groupWeights: flattenPerLayerWeights(perLayer, config.groups),
  };
}

function idsForLayer(
  groups: ReadonlyArray<FormulaGroup>,
  layer: FlowLayer,
): string[] {
  return groups.filter((g) => groupLayers(g).includes(layer)).map((g) => g.id);
}

function seedLayer(
  existing: Record<string, number>,
  ids: ReadonlyArray<string>,
): Record<string, number> {
  const total = ids.reduce((sum, id) => sum + Math.max(0, existing[id] ?? 0), 0);
  const out: Record<string, number> = {};
  const share = ids.length > 0 ? 100 / ids.length : 0;
  for (const id of ids) out[id] = total > 0 ? (existing[id] ?? 0) : share;
  return out;
}
